import { EditOutlined, PlusOutlined, SendOutlined } from "@ant-design/icons";
import { Button, Card, Space, Table, TableColumnsType, Tag } from "antd";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useGetTemplates } from "~/services/template";
import { TTemplate } from "~/types/template";
import { PreferredCommunicationMethod, TemplateType } from "~/utils/constants";
import { useNotificationStore } from "../notifications.store";
import { capitalize } from "~/utils";

const TemplateLibrary: React.FC = () => {
  const { filters, setFilters, openTemplateModal, setSelectedTemplate } =
    useNotificationStore();
  const { data: templates, isLoading } = useGetTemplates(filters);

  const columns = useMemo<TableColumnsType<TTemplate>>(
    () => [
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        render: (name: string, record) => (
          <div>
            <div style={{ fontWeight: 500 }}>{name}</div>
            <div style={{ fontSize: 12, color: "#8c8c8c" }}>
              {record.templateRef}
            </div>
          </div>
        ),
      },
      {
        title: "Type",
        dataIndex: "type",
        key: "type",
        render: (type) => (
          <Tag color={type === TemplateType.CLIENT ? "blue" : "teal"}>
            {capitalize(type)}
          </Tag>
        ),
      },
      {
        title: "Channels",
        dataIndex: "channels",
        key: "channels",
        render: (_, { channels }) => (
          <Space>
            {channels === PreferredCommunicationMethod.EMAIL ? (
              <Tag color="blue">Email</Tag>
            ) : channels === PreferredCommunicationMethod.TEXT_MESSAGE ? (
              <Tag color="purple">SMS</Tag>
            ) : (
              <>
                <Tag color="blue">Email</Tag>
                <Tag color="purple">SMS</Tag>
              </>
            )}
          </Space>
        ),
      },
      {
        title: "Triggers",
        dataIndex: "rule",
        key: "rule",
        render: (_, { rule }) => (
          <Tag
            color={
              rule?.name.toLocaleLowerCase().includes("low") ? "red" : "green"
            }
          >
            {rule?.name}
          </Tag>
        ),
      },
      {
        title: "Last Modified",
        dataIndex: "updatedAt",
        key: "updatedAt",
        render: (updatedAt) => dayjs(updatedAt).format("MMM D, YYYY"),
      },
      {
        title: "Actions",
        key: "actions",
        render: (_, row) => (
          <Space>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => openTemplateModal(row.id)}
            />
            <Button type="text" icon={<SendOutlined />} />
          </Space>
        ),
      },
    ],
    [openTemplateModal]
  );

  return (
    <Card
      classNames={{
        body: "p-0",
      }}
      style={{ marginBottom: 24 }}
    >
      <div className="p-4 border-b flex justify-between items-center">
        <div className="flex space-x-4">
          <button className="px-3 py-1.5 rounded bg-blue-100 text-blue-700">
            All Templates
          </button>
          <button className="px-3 py-1.5 rounded hover:bg-gray-100">
            Client
          </button>
          <button className="px-3 py-1.5 rounded hover:bg-gray-100">
            Team
          </button>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => openTemplateModal()}
        >
          New Template
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={templates?.items}
        pagination={{
          total: templates?.meta.totalItems,
          pageSize: filters.pageSize,
          current: filters.page,
          onChange: (page, pageSize) => {
            setFilters({
              page,
              pageSize,
            });
          },
          hideOnSinglePage: true,
        }}
        loading={isLoading}
        rowKey={(row) => row.id}
        onRow={(record) => ({
          className: "hover:cursor-pointer",
          onClick: () => {
            setSelectedTemplate(record);
          },
        })}
      />
    </Card>
  );
};

export default TemplateLibrary;
